import { useEffect, useMemo, useState } from "react";

import { useRtdbServerTimeOffset } from "../api/rtdbBaseApi";
import {
    useDroneConnectionRtdbDocument,
    useDroneConnectionRtdbDocuments,
} from "../api/rtdbWrappers.generated";

const ONLINE_THRESHOLD_MS = 5000;

function computeIsOnline(connection, estimatedServerTime) {
    const lastUpdate = connection?.lastUpdate;

    return (
        lastUpdate != null &&
        estimatedServerTime - lastUpdate < ONLINE_THRESHOLD_MS
    );
}

export const useDroneOnline = (droneId) => {
    const [droneConnection] = useDroneConnectionRtdbDocument(droneId);
    const serverTimeOffset = useRtdbServerTimeOffset();
    const [now, setNow] = useState(Date.now());

    // Force an evaluation of the timeout every second
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const estimatedServerTime = now + serverTimeOffset;

    return computeIsOnline(droneConnection, estimatedServerTime);
};

export const useDronesOnlineState = (droneIds) => {
    const [droneConnections, isLoading, error] =
        useDroneConnectionRtdbDocuments(droneIds, {
            type: "map",
        });
    const serverTimeOffset = useRtdbServerTimeOffset();
    const [now, setNow] = useState(Date.now());

    // Force an evaluation of the timeout every second
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const onlineStatuses = useMemo(() => {
        const estimatedServerTime = now + serverTimeOffset;

        return Object.fromEntries(
            Object.entries(droneConnections).map(
                ([droneId, droneConnection]) => [
                    droneId,
                    computeIsOnline(droneConnection, estimatedServerTime),
                ],
            ),
        );
    }, [now, droneConnections, serverTimeOffset]);

    return { onlineStatuses, isLoading, error };
};

export const useDronesOnline = (droneIds) => {
    const { onlineStatuses } = useDronesOnlineState(droneIds);
    return onlineStatuses;
};
